/**
 * RC-4 FIX: Usage Tracking Hook
 * 
 * ARCHITECTURE:
 * - Usage is ALWAYS computed on the backend from real DB collections
 * - This hook triggers a backend refresh, never computes locally
 * - Frontend only reads and displays the API response
 * 
 * REMOVED: Frontend-computed usage, localStorage sync, hardcoded defaults
 */
import { useEffect, useCallback, useRef } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useClinicScope } from './useClinicScope';

/**
 * Hook to keep usage stats current by refreshing from backend
 */
export const useUsageTracking = () => {
  const { clinicId } = useClinicScope();
  const {
    usage,
    updateUsage,
    checkUsageLimit,
    canPerformAction,
    isActive,
  } = useSubscription();

  const lastRefresh = useRef(0);

  /**
   * Refresh usage from backend (debounced — at most once per 10s)
   */
  const refreshUsage = useCallback(() => {
    if (!isActive || !updateUsage) return;
    const now = Date.now();
    if (now - lastRefresh.current < 10000) return; // debounce 10s
    lastRefresh.current = now;
    updateUsage();
  }, [isActive, updateUsage]);

  // Refresh on mount
  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  // Periodic refresh every 30s (not 5s — backend is the source of truth)
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(refreshUsage, 30000);
    return () => clearInterval(interval);
  }, [isActive, refreshUsage]);

  /**
   * Check if an action is allowed before performing it
   */
  const checkActionAllowed = useCallback((actionType) => {
    return canPerformAction ? canPerformAction(actionType) : { allowed: true };
  }, [canPerformAction]);

  /**
   * Get usage status for a specific limit
   */
  const getUsageStatus = useCallback((limitType) => {
    if (!checkUsageLimit) {
      return { current: 0, max: 0, percentage: 0, atLimit: false, nearLimit: false };
    }
    return checkUsageLimit(limitType);
  }, [checkUsageLimit]);

  return {
    usage,
    refreshUsage,
    checkActionAllowed,
    getUsageStatus,
    checkUsageLimit,
    canPerformAction,
  };
};

export default useUsageTracking;
